// Image path construction — single source of truth for turning DB-stored
// image names into full URLs.
//
// Environment variables:
//   VITE_IMAGES_BASE_URL  – CDN base URL for visualization assets (CloudFront domain)
//   VITE_IMAGES_ROOT      – root path prefix for visualization images (default: /images)
//   VITE_APP_IMAGES_BASE_URL – CDN base URL for app/user content (CloudFront domain + /app)

const IMAGES_BASE_URL = import.meta.env.VITE_IMAGES_BASE_URL || ''
const IMAGES_ROOT = import.meta.env.VITE_IMAGES_ROOT || '/images'
export const APP_IMAGES_BASE_URL = import.meta.env.VITE_APP_IMAGES_BASE_URL || '/shared/images'

// Directory structure variables - full URL paths (base + root + subdir)
export const IMAGE_PATHS = {
  ROOT: `${IMAGES_BASE_URL}${IMAGES_ROOT}/`,
  FOREGROUND: `${IMAGES_BASE_URL}${IMAGES_ROOT}/foreground/`,
  BACKGROUND: `${IMAGES_BASE_URL}${IMAGES_ROOT}/backgrounds/`,
  ICONS: `${IMAGES_BASE_URL}${IMAGES_ROOT}/foreground/icons/`,
  SERVICES: `${IMAGES_BASE_URL}${IMAGES_ROOT}/foreground/services/`,
  VISUALIZATIONS: `${IMAGES_BASE_URL}${IMAGES_ROOT}/foreground/visualization/`,
}

// Server-side directory paths (relative to root, no leading slash)
export const SERVER_PATHS = {
  FOREGROUND: 'foreground/',
  BACKGROUND: 'backgrounds/',
  ICONS: 'foreground/icons/',
  SERVICES: 'foreground/services/',
  VISUALIZATIONS: 'foreground/visualization/',
}

// Library type mappings for server API calls
export const LIBRARY_TYPE_PATHS: Record<string, string> = {
  icons: SERVER_PATHS.ICONS,
  services: SERVER_PATHS.SERVICES,
  backgrounds: SERVER_PATHS.BACKGROUND,
  visualizations: SERVER_PATHS.VISUALIZATIONS,
}

// Helper function to get full URL for an image
export const getImageUrl = (path: string): string => {
  // Remove leading slash if present, but preserve internal slashes
  const cleanPath = path.replace(/^\/+/, '')
  // Strip IMAGES_ROOT prefix if already present (avoid doubling)
  const rootPrefix = IMAGES_ROOT.replace(/^\/+/, '')
  if (cleanPath.startsWith(`${rootPrefix}/`)) {
    return `${IMAGES_BASE_URL}/${cleanPath}`
  }
  // Legacy: strip 'images/' prefix if present (pre-migration URLs)
  if (cleanPath.startsWith('images/')) {
    return `${IMAGES_BASE_URL}${IMAGES_ROOT}/${cleanPath.slice('images/'.length)}`
  }
  return `${IMAGES_BASE_URL}${IMAGES_ROOT}/${cleanPath}`
}

// Helper function to get directory path for a library type
export const getLibraryPath = (type: string): string => {
  return LIBRARY_TYPE_PATHS[type] || LIBRARY_TYPE_PATHS['icons']
}

// Helper function to get filesystem path (for server-side operations)
export const getFilesystemPath = (path: string): string => {
  // Remove leading slash and convert to filesystem path
  return path.replace(/^\/+/, '')
}

// ── PATH BUILDING FUNCTIONS FOR LOADING FROM DATABASE ──────────────────────────────
// Database stores filenames. We build full URLs by combining:
// - Deployment root (from env config)
// - Fixed directory paths (icons/, services/, backgrounds/)
// - Filename from database

/**
 * Build full URL from icon filename stored in database
 * @param iconFilename - Filename from database (e.g., "database.png")
 * @returns Full URL (e.g., "http://localhost:5001/images/foreground/icons/database.png")
 */
export const buildIconPath = (iconFilename?: string | null): string => {
  if (!iconFilename) return ''
  // If already a built path, return as-is
  if (iconFilename.startsWith('/images/') || iconFilename.startsWith('http'))
    return iconFilename
  const cleanName = iconFilename.replace(/^\/+/, '')
  // If path already includes the icons directory prefix, don't double it
  if (
    cleanName.startsWith('foreground/icons/') ||
    cleanName.startsWith('images/')
  ) {
    return getImageUrl(cleanName)
  }
  return getImageUrl(`${SERVER_PATHS.ICONS}${cleanName}`)
}

/**
 * Build full URL from node icon filename with vsiconblank.png fallback
 * Centralized function for all node icon handling (main palette, available palette, dependency mode)
 * Handles both regular icons (from icons/) and service images (from services/)
 * @param iconFilename - Filename from database (can be null/empty)
 * @returns Full URL with fallback to vsiconblank.png for missing icons
 */
export const buildNodeIconPath = (iconFilename?: string | null): string => {
  // Handle null, undefined, empty string, or whitespace - use vsiconblank.png
  if (
    !iconFilename ||
    (typeof iconFilename === 'string' && !iconFilename.trim())
  ) {
    return buildIconPath('vsiconblank.png')
  }

  // If already a full URL or built path, return as-is
  if (iconFilename.includes('http') || iconFilename.startsWith('/images/')) {
    return iconFilename
  }

  // If contains a slash (like "IT/database.png"), it's a service image path
  if (iconFilename.includes('/')) {
    return buildServiceImagePath(iconFilename)
  }

  // Otherwise it's a simple icon filename
  return buildIconPath(iconFilename.replace(/^\/+/, ''))
}

/**
 * Build full URL from service image path stored in database
 * @param servicePath - Customer path from database (e.g., "IT/database.png")
 * @returns Full URL (e.g., "http://localhost:5001/images/foreground/services/IT/database.png")
 */
export const buildServiceImagePath = (servicePath?: string | null): string => {
  if (!servicePath) return ''
  // If already a built path, return as-is
  if (servicePath.startsWith('/images/') || servicePath.startsWith('http'))
    return servicePath
  const cleanPath = servicePath.replace(/^\/+/, '')
  // If path already includes the services directory prefix, don't double it
  if (
    cleanPath.startsWith('foreground/services/') ||
    cleanPath.startsWith('images/')
  ) {
    return getImageUrl(cleanPath)
  }
  return getImageUrl(`${SERVER_PATHS.SERVICES}${cleanPath}`)
}

/**
 * Build full URL from background image path stored in database
 * @param backgroundPath - Customer path from database (e.g., "IT/floor1.png")
 * @returns Full URL (e.g., "http://localhost:5001/images/backgrounds/IT/floor1.png")
 */
export const buildBackgroundImagePath = (
  backgroundPath?: string | null
): string => {
  if (!backgroundPath) return ''
  // If already a built path, return as-is
  if (
    backgroundPath.startsWith('/images/') ||
    backgroundPath.startsWith('http')
  )
    return backgroundPath
  const cleanPath = backgroundPath.replace(/^\/+/, '')
  // If path already includes the backgrounds directory prefix, don't double it
  if (cleanPath.startsWith('backgrounds/') || cleanPath.startsWith('images/')) {
    return getImageUrl(cleanPath)
  }
  return getImageUrl(`${SERVER_PATHS.BACKGROUND}${cleanPath}`)
}

// Legacy exports for backward compatibility - to be removed after full migration
export const getIconUrl = buildIconPath
export const getNodeIconUrl = buildNodeIconPath
export const getServiceImageUrl = buildServiceImagePath
export const getBackgroundImageUrl = buildBackgroundImagePath

// ── COMMON PATH FUNCTIONS FOR SAVING JSON ────────────────────────────────

/**
 * Extract icon name from full URL or keep as-is if already just a name
 * Used when saving icons to JSON (dependency and service mode)
 * @param iconPath - Full URL or just icon name
 * @returns Just the icon filename (e.g., "database.png")
 */
export const extractIconName = (iconPath?: string | null): string => {
  if (!iconPath) return ''
  // If it's already just a filename (no slashes), return as-is
  if (!iconPath.includes('/')) return iconPath
  // Extract filename from URL
  return iconPath.split('/').pop() || ''
}

/**
 * Extract customer path from full service image URL
 * Used when saving service images to JSON (service mode only)
 * @param imageUrl - Full URL or customer path
 * @returns Customer path (e.g., "IT/database.png")
 */
export const extractServiceImagePath = (imageUrl?: string | null): string => {
  if (!imageUrl) return ''
  // If it's already a customer path (contains / but not http), return as-is
  if (imageUrl.includes('/') && !imageUrl.startsWith('http')) return imageUrl
  // Extract customer path from URL (everything after /services/)
  const servicesIndex = imageUrl.indexOf('/services/')
  if (servicesIndex !== -1) {
    return imageUrl.substring(servicesIndex + '/services/'.length)
  }
  // Fallback: extract just the filename
  return imageUrl.split('/').pop() || ''
}

/**
 * Extract customer path from full background image URL
 * Used when saving background images to JSON (service mode only)
 * @param imageUrl - Full URL or customer path
 * @returns Customer path (e.g., "Plant/Plant.png" or "Plant.png")
 */
export const extractBackgroundImagePath = (
  imageUrl?: string | null
): string => {
  if (!imageUrl) return ''
  // If it's already a customer path (contains / but not http and not /images/), return as-is
  if (
    imageUrl.includes('/') &&
    !imageUrl.startsWith('http') &&
    !imageUrl.includes('/images/')
  ) {
    return imageUrl
  }
  // Extract customer path from URL (everything after /backgrounds/)
  const backgroundIndex = imageUrl.indexOf('/backgrounds/')
  if (backgroundIndex !== -1) {
    return imageUrl.substring(backgroundIndex + '/backgrounds/'.length)
  }
  // Fallback: extract just the filename
  return imageUrl.split('/').pop() || ''
}

// ── APP-CONTENT IMAGE URLS (Store 2: avatars, logos, locations, etc.) ────────

/**
 * Build full URL for an app-content image (avatars, logos, locations, photos, etc.)
 * @param subdir - Subdirectory (e.g., "avatars", "logos", "locations")
 * @param filename - Filename (e.g., "user-1.png")
 * @returns Full URL via CloudFront or local /shared/images fallback
 */
export const buildAppImageUrl = (subdir: string, filename: string): string => {
  if (!filename) return ''
  if (filename.startsWith('http')) return filename
  if (filename.startsWith('/shared/images/')) {
    const relativePath = filename.replace('/shared/images/', '')
    return `${APP_IMAGES_BASE_URL}/${relativePath}`
  }
  return `${APP_IMAGES_BASE_URL}/${subdir}/${filename}`
}

/**
 * Rewrite a stored /shared/images URL to the CDN URL
 * Used for DB values like avatar_url that store full paths
 */
export const rewriteAppImageUrl = (storedUrl: string | null | undefined): string => {
  if (!storedUrl) return ''
  if (storedUrl.startsWith('http')) return storedUrl
  if (storedUrl.startsWith('/shared/images/')) {
    const relativePath = storedUrl.replace('/shared/images/', '')
    return `${APP_IMAGES_BASE_URL}/${relativePath}`
  }
  return storedUrl
}
